import React from 'react';
import { GlassWater, AlertTriangle, Zap, Droplets, Heart } from 'lucide-react';

/**
 * Preview Smoothies Page
 * Read-only version with disabled interactions
 */

const SMOOTHIE_SECTIONS = [
  {
    id: 'low-appetite',
    title: 'Low Appetite Support',
    description: 'When eating feels difficult, smoothies can provide nutrition in a more manageable form.',
    icon: Heart,
    points: [
      'Smaller volumes can deliver concentrated nutrition',
      'Easier to consume than solid foods for some people',
      'Can be sipped gradually throughout the day',
      'Temperature and texture can be adjusted to preference'
    ],
    color: 'rose'
  },
  {
    id: 'fatigue',
    title: 'Fatigue-Friendly Options',
    description: 'Simple approaches that require minimal energy to prepare.',
    icon: Zap,
    points: [
      'Keep ingredients pre-portioned when energy is higher',
      'Simple combinations can be just as nourishing',
      'Consider ready-made bases as a starting point',
      'Accept that "good enough" is often enough'
    ],
    color: 'amber'
  },
  {
    id: 'hydration',
    title: 'Hydration Support',
    description: 'Smoothies can contribute to daily fluid intake alongside water.',
    icon: Droplets,
    points: [
      'Liquid-based foods count toward hydration',
      'Can be helpful when plain water is unappealing',
      'Electrolyte balance matters - discuss with your team',
      'Some fruits have high water content naturally'
    ],
    color: 'blue'
  }
];

const getColorClasses = (color) => {
  const colors = {
    rose: { bg: 'bg-rose-50', icon: 'text-rose-600', border: 'border-rose-200' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', border: 'border-amber-200' },
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-200' }
  };
  return colors[color] || colors.blue;
};

const PreviewSmoothiesPage = () => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <GlassWater className="h-8 w-8 text-primary" />
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900">
            Smoothies
          </h1>
        </div>
        <p className="text-lg leading-relaxed text-slate-700">
          Educational information about smoothies for nutritional support
        </p>
      </div>

      {/* Educational Introduction */}
      <div className="bg-gradient-to-br from-teal-50 to-blue-50 border border-teal-200 rounded-2xl p-6 md:p-8">
        <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
          About smoothies for nutritional support
        </h2>
        <div className="space-y-4 text-base md:text-lg text-slate-700 leading-relaxed">
          <p>
            Smoothies are sometimes discussed as a way to support nutrition when appetite 
            is low or eating solid foods feels challenging. They can be a convenient way 
            to consume nutrients in a form that some people find easier to manage.
          </p>
          <p>
            This section provides general educational information about how smoothies 
            might fit into a supportive nutrition approach. It does not contain specific 
            recipes or ingredient lists.
          </p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <div className="flex gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-amber-900 mb-2">Important Notice</h3>
            <p className="text-amber-800 leading-relaxed">
              Nutritional needs are highly individual, especially during treatment. 
              Some ingredients may interact with medications or be unsuitable for your 
              situation. Always discuss dietary changes with your healthcare team or 
              a registered dietitian.
            </p>
          </div>
        </div>
      </div>

      {/* Content Sections - Read-only */}
      <div className="space-y-6 opacity-75">
        {SMOOTHIE_SECTIONS.map((section) => {
          const Icon = section.icon;
          const colors = getColorClasses(section.color);
          
          return (
            <div key={section.id} className={`bg-white rounded-2xl p-6 border ${colors.border} shadow-sm`}>
              <div className="flex items-start gap-4 mb-4">
                <div className={`p-3 ${colors.bg} rounded-xl`}>
                  <Icon className={`h-6 w-6 ${colors.icon}`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    {section.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {section.description}
                  </p>
                </div>
              </div>
              
              <ul className="space-y-2 ml-2">
                {section.points.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-slate-700">
                    <span className={`${colors.icon} mt-1`}>•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-400 mt-3">Available in full app</p>
            </div>
          );
        })}
      </div>

      {/* General Considerations */}
      <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 opacity-75">
        <h3 className="text-xl font-semibold text-slate-900 mb-4">General Considerations</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-4 border border-slate-100">
            <h4 className="font-medium text-slate-900 mb-2">Food Safety</h4>
            <p className="text-sm text-slate-600">
              Wash all produce thoroughly. Consider food safety guidelines, 
              especially if your immune system is affected.
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-100">
            <h4 className="font-medium text-slate-900 mb-2">Medication Interactions</h4>
            <p className="text-sm text-slate-600">
              Some fruits (especially grapefruit and citrus) can interact with 
              certain medications. Check with your pharmacist.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewSmoothiesPage;
